import React, { useEffect, useState } from 'react';
import { UserService } from '../../services/user.service';
import { InstitutionService } from '../../services/institution.service';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
    Modal,
    Button,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    IconButton,
    Stack,
    Pagination,
    Autocomplete,
    TextField,
} from '@mui/material';
import { ManageAccounts, Add, LockReset, ToggleOn, ToggleOff, Delete, Security, PhonelinkLock } from '@mui/icons-material';
import { UserType } from '../../interfaces/userType.enum';
import { IInstitution } from '../../interfaces/institution.interface';
import { AuthService } from '../../services/auth.service';
import { displayUsername } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';
import { useRoleLabel } from './useRoleLabel';
import SeatMeter from './SeatMeter';
import { usePlan } from '../../context/PlanContext';
import { useFreeTierNudge } from '../shared/FreeTierNudge';

interface User {
    id: string;
    username: string;
    phone: string;
    type: UserType;
    isActive: boolean;
}

const UserList = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [institutions, setInstitutions] = useState<IInstitution[]>([]);
    const [selectedInstitution, setSelectedInstitution] = useState<IInstitution | null>(null);
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [isTwoFactorResetModalOpen, setIsTwoFactorResetModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedType, setSelectedType] = useState<UserType | ''>('');
    const navigate = useNavigate();
    const { isAuthenticated, selectedInstitutionId } = useAuth();
    const { refresh: refreshPlan } = usePlan();
    const freeTier = useFreeTierNudge();
    const toast = useToast();
    const { t } = useTranslation(['users', 'common']);
    const roleLabel = useRoleLabel();
    const isSystemAdmin = AuthService.getCurrentUser()?.roles === 'SystemAdmin';
    const permissionsInstitutionId = selectedInstitution?.id ?? AuthService.getSelectedInstitution();
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });
    const [page,] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        if (isSystemAdmin) {
            fetchInstitutions();
        }
    }, [isAuthenticated, navigate, page]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchInstitutionUsers(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitution, debouncedSearch, isAuthenticated]);

    const fetchInstitutions = async () => {
        try {
            const response = await InstitutionService.getInstitutions(0, 20);
            setInstitutions(response.content || []);
        } catch (err: any) {
            toast.showApiError(err, t('manage.toasts.loadInstitutionsFailed'));
        }
    };

    const fetchInstitutionUsers = async (page: number = 0) => {
        try {
            const response = await UserService.getInstitutionUsers(page, 10, selectedInstitution?.id, debouncedSearch);
            setUsers(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err: any) {
            toast.showApiError(err, t('manage.toasts.loadUsersFailed'));
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (user: User) => {
        try {
            await UserService.toggleUserActive(user.id);
            toast.showSuccess(user.isActive !== false ? t('manage.toasts.userDeactivated') : t('manage.toasts.userActivated'));
            refreshPlan();
            await fetchInstitutionUsers(pageInfo.currentPage);
        } catch (error: any) {
            if (!freeTier.handle(error)) toast.showApiError(error, t('manage.toasts.updateStatusFailed'));
        }
    };

    const handleOpenDeleteModal = (user: User) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedUser(null);
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        try {
            await UserService.deleteUser(selectedUser.id);
            toast.showSuccess(t('manage.toasts.userDeleted'));
            refreshPlan();
            await fetchInstitutionUsers(pageInfo.currentPage);
            handleCloseDeleteModal();
        } catch (error: any) {
            console.error('Error deleting user:', error);
            toast.showApiError(error, t('manage.toasts.deleteFailed'));
            handleCloseDeleteModal();
        }
    };

    const handleOpenTypeModal = (user: User) => {
        setSelectedUser(user);
        setSelectedType(user.type);
        setIsTypeModalOpen(true);
    };

    const handleCloseTypeModal = () => {
        setIsTypeModalOpen(false);
        setSelectedUser(null);
        setSelectedType('');
    };

    const handleOpenResetPasswordModal = (user: User) => {
        setSelectedUser(user);
        setIsResetPasswordModalOpen(true);
    };

    const handleCloseResetPasswordModal = () => {
        setIsResetPasswordModalOpen(false);
        setSelectedUser(null);
    };

    const handleTypeChange = async () => {
        if (!selectedUser || !selectedType) return;

        try {
            await UserService.updateUserType(selectedUser.id, selectedType);
            toast.showSuccess(t('manage.toasts.typeUpdated'));
            refreshPlan();
            await fetchInstitutionUsers();
            handleCloseTypeModal();
        } catch (error: any) {
            console.error('Error updating user type:', error);
            if (!freeTier.handle(error)) toast.showApiError(error, t('manage.toasts.typeUpdateFailed'));
            handleCloseTypeModal();
        }
    };

    const handleResetPassword = async () => {
        if (!selectedUser) return;

        try {
            await UserService.resetUserPassword(selectedUser.id);
            toast.showSuccess(t('manage.toasts.passwordReset'));
            handleCloseResetPasswordModal();
        } catch (error) {
            console.error('Error resetting password:', error);
            toast.showApiError(error, t('manage.toasts.resetPasswordFailed'));
        }
    };

    const handleOpenTwoFactorResetModal = (user: User) => {
        setSelectedUser(user);
        setIsTwoFactorResetModalOpen(true);
    };

    const handleCloseTwoFactorResetModal = () => {
        setIsTwoFactorResetModalOpen(false);
        setSelectedUser(null);
    };

    const handleResetTwoFactor = async () => {
        if (!selectedUser) return;
        try {
            await UserService.resetTwoFactor(selectedUser.id);
            toast.showSuccess(t('manage.toasts.twoFactorReset'));
            handleCloseTwoFactorResetModal();
        } catch (error) {
            toast.showApiError(error, t('manage.toasts.resetTwoFactorFailed'));
        }
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchInstitutionUsers(page - 1); // API uses 0-based indexing
    };

    if (loading) return <div data-testid="loading-state">{t('manage.loading')}</div>;

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>{t('userList.title')}</h2>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => navigate('/users/create')}
                >
                    {t('userList.createUser')}
                </Button>
            </div>
            {/* The plan is the selected institution's; hide it while a SystemAdmin filters to another one. */}
            {(!selectedInstitution || selectedInstitution.id === selectedInstitutionId) && <SeatMeter />}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: '20px' }}>
                {isSystemAdmin && (
                    <Autocomplete
                        options={institutions}
                        getOptionLabel={(option) => option.name}
                        value={selectedInstitution}
                        onChange={(_e, newValue) => setSelectedInstitution(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} label={t('manage.filterByInstitution')} size="small" />
                        )}
                        sx={{ width: 300 }}
                    />
                )}
                <TextField
                    label={t('manage.searchByUsername')}
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: 260 }}
                />
                <span>{t('manage.totalUsers', { total: pageInfo.totalElements })}</span>
            </Box>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>{t('manage.columns.username')}</th>
                        <th style={tableHeaderStyle}>{t('manage.columns.phone')}</th>
                        <th style={tableHeaderStyle}>{t('manage.columns.type')}</th>
                        <th style={tableHeaderStyle}>{t('manage.columns.status')}</th>
                        <th style={tableHeaderStyle}>{t('manage.columns.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => (
                        <tr key={user.username}>
                            <td style={tableCellStyle}>{displayUsername(user.username)}</td>
                            <td style={tableCellStyle}>{user.phone}</td>
                            <td style={tableCellStyle}>{roleLabel(user.type)}</td>
                            <td style={tableCellStyle}>
                                <span style={{ color: user.isActive !== false ? 'green' : 'red' }}>
                                    {user.isActive !== false ? t('common:status.active') : t('common:status.inactive')}
                                </span>
                            </td>
                            <td style={tableCellStyle}>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={user.isActive !== false ? t('manage.deactivateUser') : t('manage.activateUser')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleToggleActive(user)}
                                            color={user.isActive !== false ? "success" : "default"}
                                            aria-label={user.isActive !== false ? t('manage.deactivateUser') : t('manage.activateUser')}
                                        >
                                            {user.isActive !== false ? <ToggleOn /> : <ToggleOff />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('manage.changeType')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenTypeModal(user)}
                                            color="primary"
                                            aria-label={t('manage.changeType')}
                                        >
                                            <ManageAccounts />
                                        </IconButton>
                                    </Tooltip>
                                    {isSystemAdmin && (
                                        <Tooltip title={t('manage.deleteUser')} arrow>
                                            <IconButton
                                                size="small"
                                                aria-label={t('manage.deleteUser')}
                                                onClick={() => handleOpenDeleteModal(user)}
                                                color="error"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title={t('manage.resetPassword')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenResetPasswordModal(user)}
                                            color="secondary"
                                            aria-label={t('manage.resetPassword')}
                                        >
                                            <LockReset />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('manage.resetTwoFactor')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenTwoFactorResetModal(user)}
                                            color="primary"
                                            aria-label={t('manage.resetTwoFactor')}
                                        >
                                            <PhonelinkLock />
                                        </IconButton>
                                    </Tooltip>
                                    {permissionsInstitutionId && (
                                        <Tooltip title={t('userList.managePermissions')} arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/users/${user.id}/permissions?institutionId=${permissionsInstitutionId}`)}
                                                color="info"
                                                aria-label={t('userList.managePermissions')}
                                            >
                                                <Security />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Delete Confirmation Modal */}
            <Modal
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                aria-describedby="delete-user-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>{t('manage.deleteUser')}</h3>
                    <p>{t('manage.deleteConfirm', { username: displayUsername(selectedUser?.username) })}</p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseDeleteModal} variant="outlined">
                            {t('common:actions.cancel')}
                        </Button>
                        <Button onClick={handleDeleteUser} variant="contained" color="error">
                            {t('common:actions.delete')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Type Change Modal */}
            <Modal
                open={isTypeModalOpen}
                onClose={handleCloseTypeModal}
                aria-describedby="change-type-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>{t('manage.changeUserType')}</h3>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>{t('manage.userType')}</InputLabel>
                        <Select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value as UserType)}
                            label={t('manage.userType')}
                        >
                            {Object.values(UserType).map((type) => (
                                <MenuItem key={type} value={type}>
                                    {roleLabel(type)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseTypeModal} variant="outlined">
                            {t('common:actions.cancel')}
                        </Button>
                        <Button onClick={handleTypeChange} variant="contained" color="primary">
                            {t('manage.saveChanges')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Reset Password Modal */}
            <Modal
                open={isResetPasswordModalOpen}
                onClose={handleCloseResetPasswordModal}
                aria-describedby="reset-password-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>{t('manage.resetPassword')}</h3>
                    <p>{t('manage.resetPasswordConfirm', { username: displayUsername(selectedUser?.username) })}</p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseResetPasswordModal} variant="outlined">
                            {t('common:actions.cancel')}
                        </Button>
                        <Button onClick={handleResetPassword} variant="contained" color="secondary">
                            {t('manage.resetPassword')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Two-Factor Reset Modal */}
            <Modal
                open={isTwoFactorResetModalOpen}
                onClose={handleCloseTwoFactorResetModal}
                aria-describedby="reset-2fa-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>{t('manage.resetTwoFactor')}</h3>
                    <p>
                        {t('userList.resetTwoFactorConfirm', { username: displayUsername(selectedUser?.username) })}
                    </p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseTwoFactorResetModal} variant="outlined">
                            {t('common:actions.cancel')}
                        </Button>
                        <Button onClick={handleResetTwoFactor} variant="contained" color="error">
                            {t('common:actions.reset')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {freeTier.element}
        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default UserList;
